import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '@/lib/supabaseClient';
import AppLayout from '../../components/AppLayout';

export default function AuthCallbackPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState('');

  const pendingRole = searchParams.get('role') || localStorage.getItem('kc_pending_role') || '';

  useEffect(() => {
    async function handleCallback() {
      try {
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();

        if (sessionError || !session) {
          setError('로그인 세션을 확인할 수 없습니다. 다시 시도해주세요.');
          setTimeout(() => navigate('/app/login'), 2000);
          return;
        }

        const userId = session.user.id;
        const userEmail = session.user.email || '';
        const userName = session.user.user_metadata?.name || session.user.user_metadata?.full_name || '';
        const userPhone = session.user.user_metadata?.phone || '';

        // profiles 테이블에서 사용자 조회
        const { data: profile } = await supabase
          .from('profiles')
          .select('id, roles, role, name, phone')
          .eq('id', userId)
          .maybeSingle();

        // roles(배열)가 null이면 role(텍스트)을 배열로 변환
        const resolvedRoles: ('patient' | 'caregiver')[] = profile?.roles?.length
          ? (profile.roles as ('patient' | 'caregiver')[])
          : (profile?.role ? [profile.role as ('patient' | 'caregiver')] : []);

        if (profile && resolvedRoles.length > 0) {
          // 기존 회원 → 홈으로
          localStorage.removeItem('kc_pending_role');
          if (resolvedRoles.length === 1) {
            const homePath = resolvedRoles[0] === 'caregiver' ? '/app/caregiver/home' : '/app/patient/home';
            navigate(homePath, { replace: true });
          } else {
            navigate('/app/select-role', { replace: true });
          }
          return;
        }

        // 신규 회원 → 회원가입 페이지로
        // 카카오에서 받은 정보를 미리 채워서 전달
        navigate('/app/register', {
          replace: true,
          state: {
            preselectedRole: pendingRole || 'patient',
            kakaoUserId: userId,
            kakaoName: userName,
            kakaoEmail: userEmail,
            kakaoPhone: userPhone,
          },
        });
      } catch (err) {
        console.error('Auth callback error:', err);
        setError('로그인 처리 중 오류가 발생했습니다.');
        setTimeout(() => navigate('/app/login'), 2000);
      }
    }

    handleCallback();
  }, [navigate, pendingRole]);

  return (
    <AppLayout>
      <div className="min-h-screen flex flex-col items-center justify-center bg-white px-6">
        <div className="text-center">
          {error ? (
            <>
              <div className="w-16 h-16 rounded-2xl bg-red-50 flex items-center justify-center mx-auto mb-4">
                <i className="ri-error-warning-line text-2xl text-red-500" />
              </div>
              <p className="text-sm text-red-500 font-medium">{error}</p>
              <p className="text-xs text-gray-400 mt-2">잠시 후 로그인 페이지로 이동합니다...</p>
            </>
          ) : (
            <>
              <div className="w-16 h-16 rounded-2xl bg-[#FFF8F3] flex items-center justify-center mx-auto mb-4">
                <i className="ri-loader-4-line animate-spin text-2xl text-[#FF8A65]" />
              </div>
              <p className="text-base font-bold text-gray-900">로그인 처리 중...</p>
              <p className="text-sm text-gray-500 mt-1">잠시만 기다려주세요</p>
            </>
          )}
        </div>
      </div>
    </AppLayout>
  );
}